import { useState, useEffect, lazy, Suspense, Component } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LandingPage   from "./components/LandingPage";
import AuthScreen    from "./components/AuthScreen";
import MenuScreen    from "./components/MenuScreen";
import CartScreen    from "./components/CartScreen";
import PaymentScreen from "./components/PaymentScreen";
import PolicyScreen from "./components/PolicyScreen";
import SuccessScreen from "./components/SuccessScreen";
import BottomNav from "./components/BottomNav";
import { API } from "./api";
import "./App.css";

const OrderTracker  = lazy(() => import("./components/OrderTracker"));
const AdminPanel    = lazy(() => import("./components/AdminPanel"));
const ProfileScreen = lazy(() => import("./components/ProfileScreen"));
const StatsScreen   = lazy(() => import("./components/StatsScreen"));
const OrderHistory  = lazy(() => import("./components/OrderHistory"));
const FavoritesScreen = lazy(() => import("./components/FavoritesScreen"));
const FloatingOrderButton = lazy(() => import("./components/FloatingOrderButton"));

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center", background: "#FAFAF8" }}>
          <div style={{ marginBottom: 16 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D94F00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#1C1C1C", marginBottom: 8 }}>Something went wrong</div>
          <div style={{ fontSize: 14, color: "#999", marginBottom: 24 }}>An unexpected error occurred. Please try again.</div>
          <button onClick={() => { this.setState({ hasError: false }); window.location.hash = ""; }} style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: "#D94F00", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Reload App</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [screen,    setScreen]    = useState("landing");
  const [activeTab, setActiveTab] = useState("menu"); // For bottom nav
  const [user,      setUser]      = useState(null);
  const [cart,      setCart]      = useState({});
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("fb_user");
      if (saved) {
        const parsed = JSON.parse(saved);
        setUser(parsed);
        setScreen(parsed.role === "admin" ? "admin" : "menu");
        // Verify role from server — do not rely solely on localStorage
        const token = localStorage.getItem("fb_token");
        if (token) {
          fetch(`${API}/auth/me`, {
            headers: { Authorization: "Bearer " + token }
          }).then(r => r.json()).then(fresh => {
            if (fresh && fresh.id) {
              setUser(fresh);
              localStorage.setItem("fb_user", JSON.stringify(fresh));
            }
          }).catch(e => console.error("Session refresh failed:", e));
        }
      }
    } catch (e) { console.error("Failed to restore user session:", e); localStorage.removeItem("fb_user"); }

    try {
      const savedCart = localStorage.getItem("fb_cart");
      if (savedCart) setCart(JSON.parse(savedCart));
    } catch (e) { console.error("Failed to restore cart:", e); localStorage.removeItem("fb_cart"); }

    const hash = window.location.hash;
    if (hash.includes("/success") && hash.includes("order_id=")) {
      const orderId = new URLSearchParams(hash.split("?")[1]).get("order_id");
      const pending = (() => { try { return JSON.parse(sessionStorage.getItem("pendingOrder") || "{}"); } catch { return {}; } })();
      sessionStorage.removeItem("pendingOrder");
      sessionStorage.removeItem("pendingTxnId");
      window.location.hash = "";
      const token = localStorage.getItem("fb_token");
      fetch(`${API}/orders/history`, {
        headers: token ? { Authorization: "Bearer " + token } : {}
      }).then(r => r.json()).then(orders => {
        const confirmed = Array.isArray(orders) && orders.find(o => o.order_id === orderId && o.status !== "pending");
        if (confirmed) {
          setCart({});
          setOrderData({ ...pending, order_id: orderId, razorpay_order_id: orderId });
          setScreen("success");
        } else {
          setScreen("menu");
        }
      }).catch((e) => { console.error("Failed to verify order on redirect:", e); setScreen("menu"); });
    } else if (hash.includes("/failed")) {
      setScreen("menu");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("fb_cart", JSON.stringify(cart));
  }, [cart]);

  const handleAuth = (userData) => {
    setUser(userData);
    setScreen(userData.role === "admin" ? "admin" : "menu");
  };

  const handleLogout = () => {
    localStorage.removeItem("fb_user");
    localStorage.removeItem("fb_token");
    localStorage.removeItem("fb_cart");
    setUser(null); setCart({}); setOrderData(null); setScreen("landing");
  };

  useEffect(() => {
    const handleGlobalLogout = () => {
      handleLogout();
    };
    window.addEventListener("foody-logout", handleGlobalLogout);
    return () => {
      window.removeEventListener("foody-logout", handleGlobalLogout);
    };
  }, []);


  const handleUserUpdate = (updated) => { setUser(updated); };

  const addItem = (item, selectedAddons = []) => {
    setCart(c => {
      const prev = c[item.id];
      return {
        ...c,
        [item.id]: {
          ...item,
          selectedAddons,
          qty: (prev?.qty || 0) + 1,
        },
      };
    });
  };

  const removeItem = (id) =>
    setCart(c => {
      if (!c[id]) return c;
      const u = { ...c };
      if (u[id].qty > 1) u[id] = { ...u[id], qty: u[id].qty - 1 };
      else delete u[id];
      return u;
    });

  const handleReorder = (items) => {
    const newCart = {};
    items.forEach(item => {
      newCart[item.id] = { ...item, selectedAddons: item.selectedAddons || [], qty: item.qty };
    });
    setCart(newCart);
  };

  const cartItems = Object.values(cart);
  const cartTotal = cartItems.reduce((s, i) => {
    const addonsPrice = (i.selectedAddons || []).reduce((a, ad) => a + ad.price, 0);
    return s + (i.price + addonsPrice) * i.qty;
  }, 0);
  const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);
  const resetOrder = () => { setCart({}); setOrderData(null); setScreen("menu"); };

  useEffect(() => {
    // Reset scroll position to top whenever switching screens or tabs
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });

    let startX = 0, startY = 0;
    const onStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onEnd = (e) => {
      if (!startX || !startY) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      const currentStartX = startX;
      startX = 0;
      startY = 0;

      // Strict edge swipe back (from screen edge < 35px) for sub-screens
      if (dx > 80 && dy < 40 && currentStartX < 35) {
        if (screen === "payment")  { setScreen("menu"); setActiveTab("cart"); }
        else if (screen === "success") { resetOrder(); }
        else if (screen === "tracker") { resetOrder(); }
        else if (screen === "stats")   { setScreen("menu"); setActiveTab("profile"); }
        else if (screen === "admin")   { setScreen("menu"); setActiveTab("menu"); }
        else if (screen === "policy")  { setScreen("menu"); }
      }
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, [screen, activeTab]);

  const suspenseFallback = (
    <div style={{ minHeight: "100vh", background: "#FAFAF8" }} />
  );

  return (
    <ErrorBoundary>
    <Suspense fallback={suspenseFallback}>
    <div className="app-root">
      <AnimatePresence mode="wait">
      {screen === "landing"  && <LandingPage  onLogin={() => setScreen("auth")} />}
      {screen === "auth"     && <AuthScreen   onBack={() => setScreen("landing")} onAuth={handleAuth} />}
      {screen === "menu"     && user && (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -15 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                style={{ width: '100%' }}
              >
                {activeTab === "menu" && (
                  <MenuScreen
                    user={user} cart={cart} cartCount={cartCount} cartTotal={cartTotal}
                    addItem={addItem} removeItem={removeItem}
                    onCartClick={()   => setActiveTab("cart")}
                    onAdminClick={()  => setScreen("admin")}
                    onLogout={handleLogout}
                    onProfileClick={() => setActiveTab("profile")}
                  />
                )}
                {activeTab === "favorites" && (
                  <FavoritesScreen
                    user={user} cart={cart} cartCount={cartCount}
                    addItem={addItem}
                    onMenuClick={() => setActiveTab("menu")}
                    onCartClick={() => setActiveTab("cart")}
                  />
                )}
                {activeTab === "orders" && (
                  <OrderHistory
                    user={user}
                    onBack={() => setActiveTab("menu")}
                    onReorder={(items) => { handleReorder(items); setActiveTab("cart"); }}
                  />
                )}
                {activeTab === "cart" && (
                  <CartScreen
                    user={user} cartItems={cartItems} cartTotal={cartTotal}
                    onBack={() => setActiveTab("menu")}
                    onCheckout={(d) => { setOrderData(d); setScreen("payment"); }}
                    addItem={addItem} removeItem={removeItem}
                  />
                )}
                {activeTab === "profile" && (
                  <ProfileScreen
                    user={user}
                    onBack={() => setActiveTab("menu")}
                    onLogout={handleLogout}
                    onUserUpdate={handleUserUpdate}
                    onStats={() => setScreen("stats")}
                    onShowPolicy={() => setScreen("policy")}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          <BottomNav 
            activeTab={activeTab} 
            onTabChange={setActiveTab}
            cartCount={cartCount}
          />
        </div>
      )}
      {screen === "payment"  && (
        <PaymentScreen orderData={orderData} onBack={() => { setScreen("menu"); setActiveTab("cart"); }} onSuccess={(d) => { setCart({}); setOrderData(d); setScreen("success"); }} />
      )}
      {screen === "success"  && (
        <SuccessScreen
          orderData={orderData}
          onNewOrder={resetOrder}
          onTrack={() => setScreen("tracker")}
        />
      )}
      {screen === "tracker"  && (
        <OrderTracker
          orderData={orderData}
          onNewOrder={resetOrder}
          onBack={resetOrder}
        />
      )}
      {screen === "admin"    && user && (
        <AdminPanel user={user} onBack={() => { setScreen("menu"); setActiveTab("menu"); }} onLogout={handleLogout} />
      )}
      {screen === "policy" && (
        <PolicyScreen onBack={() => setScreen("menu")} />
      )}
      {screen === "stats" && user && (
        <StatsScreen user={user} onBack={() => setScreen("menu")} />
      )}
      </AnimatePresence>
       
      {user && screen !== "landing" && screen !== "auth" && screen !== "payment" && screen !== "success" && screen !== "tracker" && (
        <FloatingOrderButton 
          user={user} 
          onClick={(order) => {
            setOrderData({
              name: user.name,
              phone: user.phone,
              items: order.items,
              total: order.total,
              razorpay_order_id: order.order_id,
              tokenType: order.token_type
            });
            setScreen("tracker");
          }}
        />
      )}
    </div>
    </Suspense>
    </ErrorBoundary>
  );
}
